import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../ui/dialog';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { bodyAPI } from '../../api';
import { toast } from 'sonner';

const BODY_TYPE_LABELS = {
  board: 'Board of Directors',
  audit_committee: 'Audit Committee',
  risk_committee: 'Risk Committee',
  governance_committee: 'Governance Committee',
  compensation_committee: 'Compensation Committee',
  other_committee: 'Other Committee',
  stockholders: 'Stockholders',
};

const DEFAULT_FORM = {
  name: '',
  body_type: 'board',
  description: '',
  notice_days: 7,
  quorum_config: { type: 'percentage', value: 50 },
};

const BodiesTab = ({ bodies, organizationId, fetchData }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const resetForm = () => { setForm(DEFAULT_FORM); setEditing(null); };

  const openEdit = (body) => {
    setEditing(body);
    setForm({
      name: body.name,
      body_type: body.body_type,
      description: body.description || '',
      notice_days: body.notice_days || 7,
      quorum_config: body.quorum_config || { type: 'percentage', value: 50 },
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await bodyAPI.update(editing.id, form);
        toast.success('Body updated');
      } else {
        await bodyAPI.create({ ...form, organization_id: organizationId });
        toast.success('Body created');
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch {
      toast.error('Failed to save body');
    } finally { setSaving(false); }
  };

  const handleDelete = async (bodyId) => {
    if (window.confirm('Are you sure you want to delete this body?')) {
      try {
        await bodyAPI.delete(bodyId);
        toast.success('Body deleted');
        fetchData();
      } catch { toast.error('Failed to delete body'); }
    }
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Bodies & Committees</h3>
          <p className="text-sm text-[#5A6570]">Manage board, committees, and stockholders</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#00A3A3] hover:bg-[#008585]" onClick={() => resetForm()} data-testid="add-body-btn">
              <Plus className="h-4 w-4 mr-2" />Add Body
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Body' : 'Add New Body'}</DialogTitle>
              <DialogDescription>Configure the body or committee settings</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Body Type</Label>
                <Select value={form.body_type} onValueChange={(value) => setForm({ ...form, body_type: value })}>
                  <SelectTrigger className="border-[#E2E6EA]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(BODY_TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="body-name">Name</Label>
                <Input id="body-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Audit Committee" className="border-[#E2E6EA]" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="body-description">Description</Label>
                <Textarea id="body-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border-[#E2E6EA]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="notice-days">Notice Days</Label>
                  <Input id="notice-days" type="number" value={form.notice_days} onChange={(e) => setForm({ ...form, notice_days: parseInt(e.target.value) })} className="border-[#E2E6EA]" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quorum-value">Quorum (%)</Label>
                  <Input id="quorum-value" type="number" value={form.quorum_config.value} onChange={(e) => setForm({ ...form, quorum_config: { ...form.quorum_config, value: parseInt(e.target.value) } })} className="border-[#E2E6EA]" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving || !form.name} className="bg-[#00A3A3] hover:bg-[#008585]">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {editing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {bodies.map((body) => (
          <Card key={body.id} className="border-[#E2E6EA]">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-[#1A1D21]">{body.name}</h4>
                  <p className="text-sm text-[#5A6570] mt-1">{BODY_TYPE_LABELS[body.body_type] || body.body_type}</p>
                  <div className="flex gap-4 mt-2 text-xs text-[#5A6570]">
                    <span>Notice: {body.notice_days} days</span>
                    <span>Quorum: {body.quorum_config?.value || 50}%</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(body)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(body.id)} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
};

export default BodiesTab;
